/**
 * AdminCleanupPage.js — 🚨 DESHABILITADO (Iter 64 · 2026-06).
 *
 * Esta página antes ejecutaba `/api/admin/cleanup-test-data` que vaciaba
 * indiscriminadamente las colecciones transaccionales (operations, ventas,
 * payments, client_orders, b2b_*, wallet_movements, etc.) sin distinguir
 * entre data de prueba y datos reales.
 *
 * En producción borró pasajes y ventas reales. Como medida de seguridad
 * el endpoint backend ahora responde 410 Gone y esta página solo informa
 * el estado actual de las colecciones, sin botón de ejecución.
 *
 * Una versión segura — con filtrado por `is_test=true`, backup automático
 * previo y doble confirmación con frase exacta — está en backlog.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ShieldCheck, Loader2, Eye } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCleanupPage = () => {
  const { getAuthHeaders } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/cleanup-test-data/preview`, { headers: getAuthHeaders() });
      setPreview(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cargar el preview'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []); // eslint-disable-line

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-cleanup-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-emerald-600" />
          Limpieza de datos — Bloqueada por seguridad
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Esta herramienta está temporalmente deshabilitada para proteger tu data.
        </p>
      </div>

      {/* Aviso prominente */}
      <Card
        className="p-6 border-2 border-red-300 bg-red-50"
        data-testid="cleanup-disabled-banner"
      >
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-full bg-red-100">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1 space-y-2">
            <h2 className="font-bold text-red-900 text-lg">
              Endpoint deshabilitado tras incidente
            </h2>
            <p className="text-sm text-red-800 leading-relaxed">
              La función "Borrar datos de prueba" estaba vaciando colecciones
              que <strong>contenían datos reales</strong> (operaciones, ventas,
              pasajes, pagos, B2B y movimientos de wallet) — no solo datos
              sintéticos.
            </p>
            <p className="text-sm text-red-800 leading-relaxed">
              Para evitar más pérdidas, el endpoint{' '}
              <code className="px-1.5 py-0.5 bg-white rounded border border-red-200 text-xs font-mono">
                POST /api/admin/cleanup-test-data
              </code>{' '}
              ahora responde <strong>410 Gone</strong> y este botón fue removido.
            </p>
            <div className="mt-3 p-3 bg-white rounded-lg border border-red-200 text-xs text-slate-700">
              <p className="font-bold mb-1">📞 ¿Perdiste datos en producción?</p>
              <p>
                Contacta a <strong>Emergent Support</strong> de inmediato para
                consultar si tienen un backup reciente del MongoDB que pueda
                restaurarse. Cuanto antes, mejor.
              </p>
            </div>
            <div className="mt-2 p-3 bg-white rounded-lg border border-red-200 text-xs text-slate-700">
              <p className="font-bold mb-1">🛠 Próxima versión segura (en backlog):</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Filtrado estricto por flag <code>is_test=true</code></li>
                <li>Backup automático previo a la operación</li>
                <li>Doble confirmación con frase exacta</li>
                <li>Whitelist explícita de colecciones (no genérica)</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Solo lectura — qué hay en cada colección hoy */}
      <Card className="p-5 border-0 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Eye className="w-4 h-4 text-slate-500" />
            Inventario actual de colecciones (solo lectura)
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={loadPreview}
            disabled={loading}
            data-testid="reload-preview-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Refrescar'}
          </Button>
        </div>
        {preview ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            {Object.entries(preview.would_delete || {}).map(([col, n]) => (
              <div
                key={col}
                className="p-2 bg-slate-50 rounded border border-slate-200 flex items-center justify-between"
              >
                <span className="font-mono text-slate-600">{col}</span>
                <span className="font-bold text-slate-900">{n}</span>
              </div>
            ))}
            <div className="p-2 bg-emerald-50 rounded border border-emerald-200 col-span-full flex items-center justify-between font-bold">
              <span className="text-emerald-800">Wallets activas</span>
              <span className="text-emerald-900">{preview.wallets_to_reset}</span>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Sin datos para mostrar.</p>
        )}
      </Card>
    </div>
  );
};

export default AdminCleanupPage;
